// apps/customer-frontend/contexts/ApiContext.tsx
'use client'

import Axios from 'axios'
import { useRouter } from 'next/navigation'
import { PropsWithChildren, createContext, useContext, useMemo } from 'react'
import { getRequestOrigin } from '@/lib/getRequestOrigin'
import { trackApiMetrics } from '@/lib/api/metrics'
import { track500Error } from '@/lib/api/track500'
import { reportErrorToDD } from '@/lib/analytics/datadogError'
import { setupApiStackTraceInterceptors } from './setupApiStackTraceInterceptors'

interface ApiData {
    apiGet: <T = any>(endpoint: string, params?: object) => Promise<T>
    apiPost: <T = any>(endpoint: string, params?: object) => Promise<T>
    apiPostBlob: <T = any>(endpoint: string, formData: FormData) => Promise<T>
    apiPut: <T = any>(endpoint: string, params?: object) => Promise<T>
    apiDelete: <T = any>(endpoint: string, params?: object) => Promise<T>
}

export const ApiContext = createContext<ApiData>({
    apiGet: async () => Promise.resolve({} as any),
    apiPost: async () => Promise.resolve({} as any),
    apiPostBlob: async () => Promise.resolve({} as any),
    apiPut: async () => Promise.resolve({} as any),
    apiDelete: async () => Promise.resolve({} as any),
})

export default function ApiProvider({ children }: PropsWithChildren) {
    const router = useRouter()

    const apiInstance = useMemo(() => {
        const apiInstance = Axios.create({
            baseURL: `${getRequestOrigin()}/api`,
            headers: {
                'Content-Type': 'application/json',
                'Cache-Control': 'no-cache, no-store, must-revalidate',
            },
            withCredentials: true,
        })

        setupApiStackTraceInterceptors(apiInstance)

        return apiInstance
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [router])

    const apiCall = async <T = any,>(
        method: 'get' | 'post' | 'put' | 'delete',
        endpoint: string,
        body?: object,
        contentTypeOverride?: string,
    ): Promise<T> => {
        const requestPath = `/api${endpoint}`
        const startTime = Date.now()

        try {
            const config = contentTypeOverride ? { headers: { 'Content-Type': contentTypeOverride } } : undefined
            const response = await apiInstance[method](endpoint, body, config)

            trackApiMetrics({
                requestMethod: method,
                requestPath: requestPath,
                responseStatus: response.status || 200,
                latencyMs: Date.now() - startTime,
                errorName: 'SUCCESS',
            })

            return response.data
        } catch (error: any) {
            const responseStatus = error?.response?.status || 0

            trackApiMetrics({
                requestMethod: method,
                requestPath: requestPath,
                // For things like timeouts and network errors, there will be no
                // response status.  We will use 0 to indicate that, The axiosErrorCode
                // will contain a more descriptive name of the error.
                responseStatus: responseStatus,
                latencyMs: Date.now() - startTime,
                // Reference: https://github.com/axios/axios?tab=readme-ov-file#error-types
                errorName: error?.code || 'ERR_UNKNOWN',
            })

            // Track 500 errors
            if (responseStatus >= 500) {
                track500Error({
                    endpoint: requestPath,
                    method,
                    status: responseStatus,
                    errorData: error?.response?.data,
                })
            }

            console.error('Api Error:', error)
            reportErrorToDD(error, {
                source: 'ApiContext',
                method,
                endpoint: requestPath,
                status: responseStatus,
            })
            throw error
        }
    }

    const apiGet = async <T = any,>(endpoint: string, params?: object): Promise<T> => {
        return apiCall<T>('get', endpoint, { params: params ?? {} })
    }

    const apiPost = async <T = any,>(endpoint: string, body?: object): Promise<T> => {
        return apiCall<T>('post', endpoint, body)
    }

    const apiPostBlob = async <T = any,>(endpoint: string, formData: FormData): Promise<T> => {
        return apiCall<T>('post', endpoint, formData, 'multipart/form-data')
    }

    const apiPut = async <T = any,>(endpoint: string, body?: object): Promise<T> => {
        return apiCall<T>('put', endpoint, body)
    }

    const apiDelete = async <T = any,>(endpoint: string): Promise<T> => {
        return apiCall<T>('delete', endpoint)
    }

    return (
        <ApiContext.Provider
            value={{
                apiGet,
                apiPost,
                apiPostBlob,
                apiPut,
                apiDelete,
            }}
        >
            {children}
        </ApiContext.Provider>
    )
}

export const useApi = () => useContext(ApiContext)
